import React, {
  AllHTMLAttributes,
  Fragment,
  HTMLInputTypeAttribute,
  TextareaHTMLAttributes,
  cloneElement,
  forwardRef
} from 'react'
import { FontAwesomeSvgIcon as FontAwesomeIcon } from 'react-fontawesome-svg-icon'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons/faExclamationTriangle'
import clsx from 'clsx'

import { ActionLink } from '../ActionLink/ActionLink'
import { Text } from '../Text/Text'
import { Tooltip } from '../Tooltip/Tooltip'

export interface InputProps extends Omit<AllHTMLAttributes<HTMLInputElement>, 'label'> {
  name: string
  type: HTMLInputTypeAttribute
  icon?: IconDefinition
  iconPosition?: 'left' | 'right'
  onIconClick?: () => void
  button?: React.ReactElement
  label?: React.ReactElement | string
  help?: React.ReactElement | string
  error?: React.ReactElement | string | null
  showErrorMessage?: boolean
  tooltip?: string | React.ReactNode
}

export const InputStyle = {
  icon: 'absolute inset-y-0 flex items-center',
  input: {
    _: 'px-4 py-3 text-base focus:outline-none ring-0  block w-full border-2 !rounded-lg text-text-default bg-background-default rounded-sm',
    error: ['', 'border-error-default'],
    icon: {
      left: 'pl-10',
      right: 'pr-10'
    },
    readOnly: 'bg-background-default border-border-default opacity-50 cursor-not-allowed'
  },
  label: {
    _: 'flex font-medium text-text-default',
    error: 'block !text-error-default',
    readOnly: 'text-text-alternative'
  }
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      button,
      children,
      className,
      defaultValue,
      error,
      help,
      icon,
      iconPosition = 'left',
      label,
      name,
      onIconClick,
      onKeyUp,
      readOnly,
      required,
      showErrorMessage = true,
      tooltip,
      type,
      ...props
    },
    ref
  ) => {
    let inputLabel
    let inputHelp
    let inputIcon

    if (children) {
      console.error('Do not pass children to the Infura Input component.')
      return <></>
    }

    const inputContent = (
      <input
        className={clsx(
          className,
          InputStyle.input._,
          InputStyle.input.error[~~!!error],
          icon && InputStyle.input.icon[iconPosition],
          readOnly && InputStyle.input.readOnly
        )}
        defaultValue={defaultValue}
        name={name}
        onKeyUp={onKeyUp}
        ref={ref}
        type={type}
        {...props}
        readOnly={readOnly}
      />
    )

    if (help && !error) {
      inputHelp = (
        <Text className='text-text-alternative mt-1 text-sm' theme='muted'>
          {help}
        </Text>
      )
    }

    if (label) {
      inputLabel = (
        <div className='mb-2 flex items-start justify-between '>
          <label className={clsx(InputStyle.label._, readOnly && InputStyle.label.readOnly)} htmlFor={name}>
            {label}
            {tooltip ? <Tooltip content={tooltip} /> : null}
          </label>
          {inputHelp}
        </div>
      )
    }

    if (icon) {
      inputIcon = onIconClick ? (
        <ActionLink
          as='button'
          className={clsx(InputStyle.icon, iconPosition === 'left' ? 'left-0 pl-1' : 'right-0 pr-3')}
          icon={icon}
          onClick={onIconClick}
          type='button'
        />
      ) : (
        <div
          className={clsx(
            InputStyle.icon,
            iconPosition === 'left' ? 'left-0 pl-3' : 'right-0 pr-3',
            'pointer-events-none'
          )}
        >
          <FontAwesomeIcon className='text-gray-600' icon={icon} />
        </div>
      )
    }

    const content = [
      inputIcon,
      inputContent,
      button &&
        cloneElement(button, {
          className: clsx(button.props.className)
        })
    ]

    if (iconPosition === 'right') content.reverse()

    return (
      <>
        {inputLabel}
        <div className='flex w-full'>
          <div className='relative flex flex-grow items-stretch gap-2 focus-within:z-10'>
            {content.map((el, index) => (
              <Fragment key={index}>{el}</Fragment>
            ))}
          </div>
        </div>
        {error && typeof error !== 'boolean' && showErrorMessage ? (
          <div
            className='flex items-center pt-2'
            data-testid={props['data-testid'] ? `${props['data-testid']}-error` : null}
          >
            <Text as='span' className={clsx(InputStyle.label.error)} theme='sm'>
              {error}
            </Text>
          </div>
        ) : null}
      </>
    )
  }
)

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> &
  Pick<InputProps, 'error' | 'label' | 'help' | 'tooltip' | 'showErrorMessage'>

export const Textarea = ({
  className,
  error,
  help,
  label,
  name,
  readOnly,
  showErrorMessage = true,
  tooltip,
  ...props
}: TextAreaProps) => {
  return (
    <>
      {label || (error && showErrorMessage) ? (
        <div className='mb-2 flex items-center justify-between'>
          <label className={clsx(InputStyle.label._, readOnly && InputStyle.label.readOnly)} htmlFor={name}>
            {label}
            {tooltip ? <Tooltip content={tooltip} /> : null}
          </label>
          {error && typeof error !== 'boolean' && showErrorMessage ? (
            <div className='flex items-center'>
              <FontAwesomeIcon className='mr-2 text-red-500' icon={faExclamationTriangle} />
              <Text as='span' className={clsx(InputStyle.label.error)} theme='sm'>
                {error}
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
      <textarea
        name={name}
        {...props}
        className={clsx(
          className,
          InputStyle.input._,
          InputStyle.input.error[~~!!error],
          readOnly && InputStyle.input.readOnly
        )}
        readOnly={readOnly}
      />
      {help ? (
        <Text className='mt-2' theme='muted'>
          {help}
        </Text>
      ) : null}
    </>
  )
}
